import React, { useState } from 'react';
import { Building2, Globe, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { toast } from 'react-toastify';
import companyService from '../services/company.service';
import aiService from '../services/ai.service';

const CompanyCreationForm = ({ company, onCompanyCreated, onClose }) => {
  const [step, setStep] = useState(company ? 'manual-form' : 'url-input'); // 'url-input', 'extracting', 'confirm', 'saving'
  const [companyUrl, setCompanyUrl] = useState('');
  const [extractedData, setExtractedData] = useState(null);
  const [confirmedData, setConfirmedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    if (!companyUrl.trim()) {
      toast.error('Please enter a company URL');
      return;
    }

    setLoading(true);
    setError('');
    setStep('extracting');

    try {
      // Call the AI service to extract company information
      const result = await aiService.extractCompanyInfo(companyUrl);

      if (result.success) {
        setExtractedData(result.data);
        setStep('confirm');
      } else {
        throw new Error(result.message || 'Failed to extract company information');
      }
    } catch (error) {
      console.error('Error extracting company info:', error);
      setError(error.message);
      setStep('url-input');
      toast.error('Failed to extract company information. Please try again or enter details manually.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmData = (data) => {
    setConfirmedData(data);
    setStep('saving');
    saveCompany(data);
  };

  const saveCompany = async (data) => {
    try {
      let response;
      if (company) {
        // Update existing company
        response = await companyService.updateCompany(company._id, data);
        toast.success('Company updated successfully!');
      } else {
        // Create new company
        response = await companyService.createCompany(data);
        toast.success('Company created successfully!');
      }
      
      if (response.success) {
        onCompanyCreated(response.data);
        onClose();
      } else {
        throw new Error(response.message || 'Failed to save company');
      }
    } catch (error) {
      console.error('Error saving company:', error);
      setError(error.message);
      setStep('confirm');
      toast.error('Failed to save company. Please try again.');
    }
  };

  const handleManualEntry = () => {
    setStep('manual-form');
  };

  const renderUrlInput = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <Building2 size={48} className="mx-auto text-orange-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {company ? 'Edit Company' : 'Add New Company'}
        </h2>
        <p className="text-gray-600">Enter a company website URL to automatically extract information</p>
      </div>

      <form onSubmit={handleUrlSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Website URL
          </label>
          <div className="flex gap-3">
            <input
              type="url"
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              placeholder="https://example.com"
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <Globe size={20} />
                  Extract Info
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle size={20} className="text-red-500" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={handleManualEntry}
            className="text-orange-600 hover:text-orange-700 font-medium"
          >
            Or enter company details manually
          </button>
        </div>
      </form>
    </div>
  );

  const renderExtracting = () => (
    <div className="text-center py-12">
      <Loader2 size={48} className="mx-auto text-orange-500 animate-spin mb-4" />
      <h3 className="text-xl font-semibold text-gray-900 mb-2">Extracting Company Information</h3>
      <p className="text-gray-600">Please wait while we analyze the company website...</p>
    </div>
  );

  const renderConfirmation = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Company Information</h2>
        <p className="text-gray-600">Please review and confirm the extracted information</p>
      </div>

      <CompanyDataConfirmation
        data={extractedData}
        onConfirm={handleConfirmData}
        onBack={() => setStep('url-input')}
      />
    </div>
  );

  const renderManualForm = () => (
    <ManualCompanyForm
      company={company}
      onSave={handleConfirmData}
      onBack={() => setStep('url-input')}
    />
  );

  const renderSaving = () => (
    <div className="text-center py-12">
      <Loader2 size={48} className="mx-auto text-orange-500 animate-spin mb-4" />
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {company ? 'Updating Company' : 'Creating Company'}
      </h3>
      <p className="text-gray-600">Please wait while we save the company information...</p>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'url-input':
        return renderUrlInput();
      case 'extracting':
        return renderExtracting();
      case 'confirm':
        return renderConfirmation();
      case 'manual-form':
        return renderManualForm();
      case 'saving':
        return renderSaving();
      default:
        return renderUrlInput();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X size={24} />
        </button>
        
        <div className="p-6">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

// Company Data Confirmation Component
const CompanyDataConfirmation = ({ data, onConfirm, onBack }) => {
  const [editedData, setEditedData] = useState(data);

  const handleChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleConfirm = () => {
    onConfirm(editedData);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Name *
          </label>
          <input
            type="text"
            value={editedData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industry
          </label>
          <input
            type="text"
            value={editedData.industry || ''}
            onChange={(e) => handleChange('industry', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Website
          </label>
          <input
            type="url"
            value={editedData.website || ''}
            onChange={(e) => handleChange('website', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Size
          </label>
          <input
            type="text"
            value={editedData.size || ''}
            onChange={(e) => handleChange('size', e.target.value)}
            placeholder="e.g., 50-100 employees"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Country
          </label>
          <input
            type="text"
            value={editedData.country || ''}
            onChange={(e) => handleChange('country', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address
          </label>
          <input
            type="text"
            value={editedData.address || ''}
            onChange={(e) => handleChange('address', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={editedData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className="flex-1 bg-orange-500 text-white py-2 px-4 rounded-md hover:bg-orange-600 transition-colors"
        >
          Save Company
        </button>
      </div>
    </div>
  );
};

// Manual Company Form Component
const ManualCompanyForm = ({ company, onSave, onBack }) => {
  const [formData, setFormData] = useState({
    name: company?.name || '',
    description: company?.description || '',
    industry: company?.industry || '',
    website: company?.website || '',
    address: company?.address || '',
    country: company?.country || '',
    size: company?.size || ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <Building2 size={48} className="mx-auto text-orange-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {company ? 'Edit Company Details' : 'Enter Company Details'}
        </h2>
        <p className="text-gray-600">Please fill in the company information manually</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Industry
            </label>
            <input
              type="text"
              value={formData.industry}
              onChange={(e) => handleChange('industry', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => handleChange('website', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Size
            </label>
            <input
              type="text"
              value={formData.size}
              onChange={(e) => handleChange('size', e.target.value)}
              placeholder="e.g., 50-100 employees"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Address
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
          >
            Back
          </button>
          <button
            type="submit"
            className="flex-1 bg-orange-500 text-white py-2 px-4 rounded-md hover:bg-orange-600 transition-colors"
          >
            {company ? 'Update Company' : 'Create Company'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyCreationForm; 